import { InAppState } from "../base/InAppState";
import { DragScroll } from "../../core/DragScroll";
import template from "./RecommendationsTemplate.html";

import { _lcs } from "../../i18n";

// Chip id -> what it filters and how it is labelled. "all" is synthesised.
// German source strings double as their dictionary keys; the template
// translates them (this constant is evaluated before I18n exists).
const KINDS = [
    { id : "gastro", label : "Gastronomie",        icon : "utensils" },
    { id : "poi",    label : "Sehenswürdigkeiten", icon : "landmark" },
    { id : "tour",   label : "Touren",             icon : "route" }
];

/**
 * RecommendationsState — everything the host recommends, on one screen.
 *
 * Reached from the home rail once the host has picked more than fits there.
 * Two cards to a row rather than the home screen's sideways rail: this is the
 * screen for browsing the whole set, not for glancing at the top of it.
 *
 * A sub-page, so the bottom tab bar stays (a guest should always be one tap
 * from the four tabs) but no tab is lit — the back button is what returns to
 * where they came from.
 */
export class RecommendationsState extends InAppState
{
    static ROUTE = "/recommendations";
    static ID = "recommendations";

    getActiveTab() { return null; }

    async enter()
    {
        // Shared with the home rail, so arriving here costs no second request.
        await this.app.region.loadRecommendations();

        this.counts = this.app.region.recommendationCounts();
        this.filter = RecommendationsState.lastFilter || "all";
        if ( this.filter !== "all" && !this.counts[ this.filter ] )
        {
            this.filter = "all";
        }

        this.renderScreen( template, {
            house : this.app.accommodation?.name || _lcs( "deinem Gastgeber" ),
            filters : this._filters()
        } );

        this.gridEl = this._q( '[data-id="grid"]' );
        this.emptyEl = this._q( '[data-id="empty"]' );

        this.addEventListener( this._q( '[data-id="back"]' ), "click", () => this._goBack() );
        const chips = this._q( '[data-id="filters"]' );
        this.addEventListener( chips, "click", ( e ) => this._onFilter( e ) );
        this.addEventListener( this.gridEl, "click", ( e ) => this._onCard( e ) );

        // A chip row is its own horizontal scroller and does not sit in a
        // vertically scrolling strip, so the wheel is safe to claim here.
        DragScroll.attach( this, chips );
        DragScroll.reveal( chips, chips.querySelector( ".chip--active" ) );

        this.renderCards();
    }

    /* -------------------------------- Filters -------------------------------- */

    _filters()
    {
        const filters = [ {
            id : "all", label : "Alle", icon : "sparkles",
            count : this.counts.total, isActive : this.filter === "all"
        } ];

        // Only kinds with something in them — see the template comment.
        KINDS.forEach( ( kind ) =>
        {
            if ( this.counts[ kind.id ] > 0 )
            {
                filters.push( {
                    ...kind,
                    count : this.counts[ kind.id ],
                    isActive : this.filter === kind.id
                } );
            }
        } );

        return filters;
    }

    _onFilter( e )
    {
        const btn = e.target.closest( "[data-filter]" );
        if ( !btn ) return;

        const id = btn.getAttribute( "data-filter" );
        if ( id === this.filter ) return;

        this.filter = id;
        // Remembered so returning from a detail page lands on the same subset.
        RecommendationsState.lastFilter = id;

        this._q( '[data-id="filters"]' ).querySelectorAll( "[data-filter]" ).forEach( ( chip ) =>
            chip.classList.toggle( "chip--active", chip.getAttribute( "data-filter" ) === id ) );

        this.renderCards();
    }

    /* -------------------------------- Cards ---------------------------------- */

    renderCards()
    {
        const cards = this.app.region.recommendationCards( this.filter === "all" ? null : this.filter );

        if ( cards.length === 0 )
        {
            this.gridEl.innerHTML = "";
            this.emptyEl.textContent = this.app.region.recommendationsFailed()
                ? _lcs( "Die Empfehlungen konnten nicht geladen werden." )
                : _lcs( "Deine Gastgeber haben hier noch nichts empfohlen." );
            this.emptyEl.hidden = false;
            return;
        }

        this.emptyEl.hidden = true;
        this.gridEl.innerHTML = cards.map( ( card ) => this._cardHtml( card ) ).join( "" );
    }

    _cardHtml( card )
    {
        const photo = card.image
            ? '<span class="recocard__photo ga-photo" style="background-image:url(' + this._cssUrl( card.image ) + ')"></span>'
            : '<span class="recocard__photo ga-photo ga-photo--' + this._esc( card.photo ) + '">' +
                  '<i class="icon icon--' + this._esc( card.icon ) + '"></i></span>';

        return '' +
            '<button class="recocard" type="button" data-entry="' + this._esc( card.kind ) + ':' + this._esc( card.id ) + '">' +
                photo +
                '<span class="recocard__body">' +
                    '<span class="badge badge--accent badge--sm">' + this._esc( card.cat ) + '</span>' +
                    '<span class="recocard__title">' + this._esc( card.title ) + '</span>' +
                    '<span class="recocard__meta">' + this._esc( card.distance || card.meta ) + '</span>' +
                '</span>' +
            '</button>';
    }

    _onCard( e )
    {
        const card = e.target.closest( "[data-entry]" );
        if ( !card ) return;

        const [ kind, id ] = String( card.getAttribute( "data-entry" ) ).split( ":" );
        if ( kind && id ) this.app.router.redirect( "/region/" + kind + "/" + id );
    }

    /* -------------------------------- Helpers -------------------------------- */

    _goBack()
    {
        this.app.router.redirect( "/" );
    }

    _q( selector )
    {
        return this.app.view.pageContent.querySelector( selector );
    }

    _cssUrl( url )
    {
        // Quoted and escaped: an image URL ends up inside a style attribute.
        return "'" + String( url ).replace( /['"\\\n\r]/g, "" ) + "'";
    }

    _esc( value )
    {
        return String( value === null || value === undefined ? "" : value )
            .replace( /&/g, "&amp;" )
            .replace( /</g, "&lt;" )
            .replace( />/g, "&gt;" )
            .replace( /"/g, "&quot;" );
    }
}
